'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { cn } from '~/lib/utils'
import { Button } from './button'
import { MoonIcon, SunIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip'
import { useTheme } from 'next-themes'

interface AnimatedThemeTogglerProps extends React.ComponentPropsWithoutRef<'button'> {
    duration?: number
}

export const AnimatedThemeToggler = ({ className, duration = 400, ...props }: AnimatedThemeTogglerProps) => {
    const { theme, setTheme } = useTheme()
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    const toggleTheme = useCallback(async () => {
        if (!buttonRef.current) return

        const newTheme = theme === 'dark' ? 'light' : 'dark'

        if (!document.startViewTransition) {
            setTheme(newTheme)
            return
        }

        try {
            const transition = document.startViewTransition(() => {
                flushSync(() => {
                    setTheme(newTheme)
                })
            })

            await transition.ready

            const { top, left, width, height } = buttonRef.current.getBoundingClientRect()
            const x = left + width / 2
            const y = top + height / 2
            const maxRadius = Math.hypot(
                Math.max(left, window.innerWidth - left),
                Math.max(top, window.innerHeight - top)
            )

            document.documentElement.animate(
                {
                    clipPath: [
                        `circle(0px at ${x}px ${y}px)`,
                        `circle(${maxRadius}px at ${x}px ${y}px)`
                    ]
                },
                {
                    duration,
                    easing: 'ease-in-out',
                    pseudoElement: '::view-transition-new(root)'
                }
            )
        } catch (error) {
            // Fallback if view transition fails
            console.warn('View transition failed, falling back to simple theme toggle:', error)
            setTheme(newTheme)
        }
    }, [theme, setTheme, duration])

    if (!mounted) return null

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    ref={buttonRef}
                    variant='ghost'
                    className={cn('h-auto rounded-lg p-2', className)}
                    onClick={toggleTheme}
                    {...props}
                >
                    <SunIcon className='hidden size-5 dark:block transition-transform duration-200' />
                    <MoonIcon className='size-5 dark:hidden transition-transform duration-200' />
                </Button>
            </TooltipTrigger>
            <TooltipContent>
                <p>{theme === 'dark' ? 'Chế độ sáng' : 'Chế độ tối'}</p>
            </TooltipContent>
        </Tooltip>
    )
}
